import { useTranslation } from "react-i18next";

import LocaleLink from "@/components/LocaleLink";
import { Reveal } from "@/components/ui/reveal";

/**
 * The language-model objection (Messaging Architecture v1.1, Tier 1).
 *
 * Homepage, after OneControl. That section lands the second-framework
 * problem. This one answers the next objection: a model can draft the
 * control set. We are not competing with that draft. We are the record
 * it is measured against.
 *
 * Light band on purpose. OneControl is Brand Ink; the page's rhythm is
 * alternating light and dark. The close sits on a Brand Ink shelf, the
 * same device OneControl uses for its pivot, so the two dark marks are
 * a pair rather than a second full band.
 *
 * No clock. Currency is the approved wording from currency-mechanism.md.
 * Article 11 / March is a case ("a regulation you watch"), not a bulletin.
 * The chain is named. "End to end" is not. RegGenome is the licensed source;
 * the claim is what we do with the corpus.
 *
 * /global-coverage already carries the spine and the currency band. The
 * echo there is a third contrast line (spineVsModel), not this paragraph.
 */
const LanguageModelObjection = () => {
  const { t } = useTranslation();

  return (
    <section id="the-record" className="bg-background py-16 lg:py-24">
      <div className="container mx-auto max-w-6xl px-6">
        <Reveal>
          <p className="eyebrow mb-4">{t("lmObjection.eyebrow")}</p>
          <h2 className="section-title-fluid max-w-3xl text-balance font-bold text-foreground">
            {t("lmObjection.title")}
          </h2>
          <p className="mt-6 max-w-3xl text-lg leading-relaxed text-muted-foreground">
            {t("lmObjection.lead")}
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <blockquote className="mt-10 border-t-2 border-trail-teal bg-brand-ink px-6 py-8 text-brand-ink-foreground">
            <p className="text-xl font-semibold text-balance">
              {t("lmObjection.closeLead")}
            </p>
            <p className="mt-2 max-w-3xl text-lg leading-relaxed text-brand-ink-foreground/80">
              {t("lmObjection.closeBody")}
            </p>
          </blockquote>
        </Reveal>

        <div className="mt-10 grid gap-6 md:grid-cols-2">
          <Reveal delay={0.15}>
            <div className="h-full border border-border bg-background p-6">
              <h3 className="mb-3 text-lg font-semibold text-foreground">
                {t("lmObjection.currencyTitle")}
              </h3>
              <p className="leading-relaxed text-muted-foreground">
                {t("lmObjection.currency")}
              </p>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                {t("lmObjection.source")}
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="h-full border border-border bg-background p-6">
              <h3 className="mb-3 text-lg font-semibold text-foreground">
                {t("lmObjection.chainTitle")}
              </h3>
              <p className="leading-relaxed text-muted-foreground">
                {t("lmObjection.chain")}
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.25}>
          <p className="mt-10">
            <LocaleLink
              to="/global-coverage"
              className="inline-flex items-center gap-2 font-medium text-primary hover:underline"
            >
              {t("lmObjection.cta")}
              <span aria-hidden="true">→</span>
            </LocaleLink>
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default LanguageModelObjection;
